import { useCallback, useState } from "react";

import type { ExtendedThalamosUser } from "@thalamos/aspire-common";
import { isS132RecordingEnabled } from "@thalamos/aspire-common";

const storageKey = (formContextId: string) => `s132Prompt:${formContextId}`;

/**
 * Which current section a queued prompt is for: a Section 136, or the
 * patient's other section, CTO included. A Section 136 can run alongside that
 * other section, and every form that prompts concerns the other one, so the
 * prompt must not settle on whichever section is newer.
 */
export type S132PromptFor = "section136" | "detention";

const PROMPT_FOR: readonly S132PromptFor[] = ["section136", "detention"];

/**
 * The signatures after which to check that Section 132 information has been
 * given: a record of detention, a holding power or a CTO subjects the patient
 * to a provision, and the receiving part of a transfer brings them to new
 * hospital managers. Amendments re-sign an existing form and change nothing.
 */
export function signCallsForS132Prompt(args: {
  formTemplateId: string;
  part: number;
  partCount: number;
  formVersion: number;
  /** A section 2 or 3 record of detention is complete after one part. */
  isOnePartRecordOfDetention?: boolean;
}): boolean {
  if (args.formVersion > 1) return false;
  const isLastPart = args.part === args.partCount;
  switch (args.formTemplateId) {
    case "mha-h3":
      return isLastPart || !!args.isOnePartRecordOfDetention;
    case "mha-h4":
      return args.part === 2;
    // The Section 5(2) state begins at the first signature; the second, the
    // hospital managers' receipt, prompts again only while the information is
    // still owed.
    case "mha-h1":
      return true;
    case "mha-h2":
    case "mha-cto1":
      return isLastPart;
    default:
      return false;
  }
}

// Session storage carries the prompt across the navigations that follow a sign
// or an incident acceptance, and is scoped to the tab. The prompt is only a nudge, so where storage is
// unavailable it is skipped.
export function queueS132Prompt(
  user: ExtendedThalamosUser | undefined,
  formContextId: string,
  promptFor: S132PromptFor,
): void {
  if (!isS132RecordingEnabled(user)) return;
  try {
    sessionStorage.setItem(storageKey(formContextId), promptFor);
  } catch {
    return;
  }
}

export function queuedS132Prompt(
  formContextId: string,
): S132PromptFor | undefined {
  try {
    const stored = sessionStorage.getItem(storageKey(formContextId));
    return PROMPT_FOR.find((promptFor) => promptFor === stored);
  } catch {
    return undefined;
  }
}

export function clearS132Prompt(formContextId: string): void {
  try {
    sessionStorage.removeItem(storageKey(formContextId));
  } catch {
    return;
  }
}

/**
 * The section a prompt is queued for at `formContextId`, if any, and a way to
 * spend it. Storage is read on every render rather than held in state, so the
 * answer follows a change of `formContextId` without a remount.
 */
export function useQueuedS132Prompt(formContextId: string): {
  queuedFor: S132PromptFor | undefined;
  done: () => void;
} {
  const [, rerender] = useState(0);
  const done = useCallback(() => {
    clearS132Prompt(formContextId);
    rerender((n) => n + 1);
  }, [formContextId]);
  return { queuedFor: queuedS132Prompt(formContextId), done };
}
